<template>
  <div class="w-full">
    <!-- Today's risk level -->
    <div class="vx-row mb-base">
      <div class="vx-col w-full">
        <vx-card id="today-indicator" ref="today-indicator" :title="todayLevelLabel">
          <div
            v-if="
              (shouldHideTodaysLevel || indicatorCurrentLevel !== null) && downloadableDataIndicator
            "
            class="vx-row md:mb-8"
          >
            <!-- The 'key' attribute is here to refresh the component. If
            not there, an update of benchmark might not trigger the blurring.-->
            <gauge-chart
              :key="lockedTextContent"
              class="vx-col w-full self-center main"
              :hide="shouldHideTodaysLevel"
              :hide-without-lock="!isUnderlyingActive"
              :progress="
                !isUnderlyingActive
                  ? 0
                  : shouldHideTodaysLevel
                    ? Math.random() * 0.75
                    : indicatorCurrentLevel
              "
              :get-color-from-value="getRadialColorFromValue"
              :get-text-from-value="getLabelFromValue"
              :locked-redirect-url="lockedRedirectUrl"
              :locked-text-content="lockedTextContent"
              :height="250"
              :width="250"
              :thickness="25"
            >
              <template slot="title">
                <knowledge-base-link v-if="knowledgeBaseTerm" :term="knowledgeBaseTerm">
                  <h5 v-if="shortName">{{ name }} ({{ shortName }})</h5>
                  <h5 v-else>
                    {{ name }}
                  </h5>
                </knowledge-base-link>
                <h5 v-else-if="shortName">{{ name }} ({{ shortName }})</h5>
                <h5 v-else>
                  {{ name }}
                </h5>
              </template>
            </gauge-chart>
            <div class="w-full">
              <div class="flex justify-center">
                <download-as-csv
                  :data="downloadableDataIndicator"
                  :extra-params="extraParams"
                  :name="strategiesNames[0].replace('using', '') + '__risk_level'"
                  :data-downloaded-name="'historical values'"
                  with-button
                />
              </div>
            </div>
          </div>
          <div
            v-else-if="indicatorCurrentLevel === null || downloadableDataIndicator === null"
            class="text-danger"
          >
            {{ serverErrorMessage }}
          </div>
        </vx-card>
      </div>
    </div>
    <!-- Historical results -->
    <div class="vx-row mb-base">
      <div class="vx-col w-full">
        <vx-card ref="historical-results" title="Historical results">
          <div
            v-if="
              dates &&
              cumulativeReturnsWithExposureAndRiskLevel &&
              tablePerfNames &&
              tableStrategiesStats &&
              exposureData &&
              downloadableDataEndOfDayExposure &&
              outperformanceData &&
              drawdownsDataWithExposure &&
              biggestDrawdowns
            "
          >
            <div class="w-full mb-12">
              <div class="mb-8 items-center">
                <date-range-picker
                  ref="date-range-picker"
                  :dates="dates"
                  :events="events"
                  :disabled="loading"
                  @dates-change="changeDates"
                />
              </div>
            </div>
            <div id="cumulative-returns" class="my-12 relative">
              <line-chart
                ref="cumulative-returns-graph"
                :data="cumulativeReturnsWithExposureAndRiskLevel"
                :legends="strategiesNamesWithRiskAndExposure"
                :legends-number="strategiesNamesWithRiskAndExposure.length"
                :legends-to-not-show-indexes="[3]"
                :line-colors="strategiesColorsWithRiskAndExposure"
                :format-value-array="strategiesFormats"
                :lines-to-not-show="[exposurePropertyName, riskLevelPropertyName]"
                :line-key-order="cumulativeReturnsLineKeyOrder"
                :background-data="dataIndicator"
                :background-color-func="getRedGradientAreaColorFromValue"
                :background-tooltip-label-func="getLabelFromValue"
                zoom
                :dates="period"
                switch-scale
                scale-log
                :locked-redirect-url="lockedRedirectUrl"
                :locked-text-content="lockedTextContent"
                @dates-change="changeDates"
              />
            </div>
            <div id="perf-table" class="mt-12 relative">
              <performance-table
                :perf-names="tablePerfNames"
                :strategies-stats="tableStrategiesStats"
                :format-value-array="tablePerfFormats"
                :colors="strategiesColors"
                :first-date="getFirstPerfDate"
                :last-date="getLastPerfDate"
              />
            </div>
            <div id="exposure" class="mt-12 relative">
              <h5 class="mb-3">Implied end-of-day exposure</h5>
              <stacked-area-chart
                ref="stacked-chart-exposure"
                :data="exposureData"
                format-value=",.0%"
                :y-domain="[0, 1]"
                :opacity-area="0.6"
                :dates="dates"
                zoom
                :locked-redirect-url="lockedRedirectUrl"
                :locked-text-content="lockedTextContent"
                @dates-change="changeDates"
              />
              <div class="flex items-center mt-2">
                <p class="text-xs pl-8">These values should be considered as an approximation.</p>
                <download-as-csv
                  class="ml-auto hidden sm:flex"
                  :name="strategiesNames[0].replace('using', '') + '__eod_exposure'"
                  :data="downloadableDataEndOfDayExposure"
                  :data-downloaded-name="'implied end of day exposure'"
                />
              </div>
            </div>
            <div id="outperformance" class="mt-12 relative">
              <h5 class="mb-3">Relative performance</h5>
              <area-chart
                ref="area-chart-outperformance"
                :data="outperformanceData"
                format-value=".1%"
                :area-fill-color-func="backgroundFromValue"
                :tooltip-color-func="relativePerfTooltipTextColorFromValue"
                :opacity-area="0.5"
                :thresholds="[1]"
                format-y-axis=".0%"
                :tick-base="1"
                switch-scale
                scale-log
                zoom
                :locked-redirect-url="lockedRedirectUrl"
                :locked-text-content="lockedTextContent"
                :dates="period"
                @dates-change="changeDates"
              />
            </div>
            <div id="drawdowns" class="mt-12 relative">
              <h5 class="mb-3">Drawdowns</h5>
              <line-chart
                ref="line-chart-drawdowns"
                :data="drawdownsDataWithExposure"
                :legends="strategiesNames"
                :legends-number="strategiesNames.length"
                :line-colors="strategiesColors"
                :y-domain="[null, 0.0]"
                format-y-axis=",.0%"
                format-value=",.1%"
                :line-key-order="drawdownsLineKeyOrder"
                zoom
                :locked-redirect-url="lockedRedirectUrl"
                :locked-text-content="lockedTextContent"
                :dates="period"
                @dates-change="changeDates"
              />
            </div>
            <div id="biggest-drawdowns" class="mt-12 relative">
              <h5 class="mb-3">Biggest drawdowns of {{ underlyingName }}</h5>
              <vs-table class="mt-2" stripe :data="biggestDrawdowns">
                <template slot="thead">
                  <vs-th class="text-black text-sm md:text-base">{{ strategiesNames[1] }}</vs-th>
                  <vs-th class="text-black text-sm md:text-base">{{ strategiesNames[0] }}</vs-th>
                  <vs-th class="text-black text-sm md:text-base">From</vs-th>
                  <vs-th class="text-black text-sm md:text-base">To</vs-th>
                </template>
                <template>
                  <vs-tr
                    v-for="(drawdown, drawdownIndex) in biggestDrawdowns"
                    :key="drawdownIndex"
                    class="font-semibold hoverFlat"
                  >
                    <vs-td
                      class="text-sm md:text-base w-1/3"
                      :style="{ color: `${strategiesColors[1]}` }"
                    >
                      <span>{{ d3Formatter(",.2%")(drawdown.benchmark_value) }}</span>
                    </vs-td>
                    <vs-td
                      class="text-sm md:text-base w-1/3"
                      :style="{ color: `${strategiesColors[0]}` }"
                    >
                      <span>{{ d3Formatter(",.2%")(drawdown.value) }}</span>
                    </vs-td>
                    <vs-td class="text-sm md:text-base w-1/6">
                      <span>{{ formatDate(drawdown.start) }}</span>
                    </vs-td>
                    <vs-td class="text-sm md:text-base w-1/6">
                      <span>{{ formatDate(drawdown.end) }}</span>
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
              <p v-if="getFirstPerfDate && getLastPerfDate" class="text-justify mt-2 text-xs">
                Drawdowns computed from {{ getFirstPerfDate }} to {{ getLastPerfDate }}.
              </p>
            </div>
          </div>
          <div
            v-else-if="
              dates === null ||
              cumulativeReturnsWithExposureAndRiskLevel === null ||
              tablePerfNames === null ||
              tableStrategiesStats === null ||
              exposureData === null ||
              downloadableDataEndOfDayExposure === null ||
              outperformanceData === null ||
              drawdownsDataWithExposure === null ||
              biggestDrawdowns === null
            "
            class="text-danger"
          >
            {{ serverErrorMessage }}
          </div>
        </vx-card>
      </div>
    </div>
    <div v-if="timelineData && timelineData.length > 0" class="vx-row mb-base">
      <div class="vx-col w-full">
        <vx-card title="Indicator timeline">
          <vx-timeline :data="timelineData" />
        </vx-card>
      </div>
    </div>
    <div class="vx-row mb-base">
      <div class="vx-col w-full">
        <indicator-disclaimer-card />
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from "d3";
import moment from "moment";

import { TOOLTIP_DISPLAYED_EVENT, UPDATE_DATE_EVENT } from "@/assets/constants/analytics";
import { SERVER_ERROR_MESSAGE } from "@/assets/constants/global";
import {
  defaultGetLabelFromValue,
  defaultGetRadialColorFromValue,
  defaultGetRedGradientAreaColorFromValue,
  defaultGetTooltipColorCumulativeReturns,
  defaultGetTooltipTextColorFromValue,
  EXPOSURE_PROPERTY_NAME,
  INDICATOR_THRESHOLD_HIGH,
  INDICATOR_THRESHOLD_LOW,
  RISK_LEVEL_PROPERTY_NAME,
} from "@/assets/constants/indicators/alquant";
import exports from "@/assets/scss/exports.module.scss";
import IndicatorDisclaimerCard from "@/components/cards/IndicatorDisclaimerCard.vue";
import DateRangePicker from "@/components/datepickers/DateRangePicker.vue";
import DownloadAsCSV from "@/components/extras/DownloadAsCsv.vue";
import AreaChart from "@/components/graphs/AreaChart.vue";
import GaugeChart from "@/components/graphs/GaugeChart.vue";
import LineChart from "@/components/graphs/LineChart.vue";
import StackedAreaChart from "@/components/graphs/StackedAreaChart.vue";
import KnowledgeBaseLink from "@/components/links/KnowledgeBaseLink.vue";
import PerformanceTable from "@/components/tables/PerformanceTable.vue";
import VxTimeline from "@/components/vx-timeline/VxTimeline";

// All references to graphs that contain a zoom picker.
const REFS_ZOOMABLE_CHARTS = [
  "cumulative-returns-graph",
  "stacked-chart-exposure",
  "area-chart-outperformance",
  "line-chart-drawdowns",
];

// All IDs of containers that need to show a loading effect when the data is loading.
/*
const IDS_LOADING_CONTAINERS = [
  "cumulative-returns",
  "perf-table",
  "exposure",
  "outperformance",
  "drawdowns",
  "biggest-drawdowns",
];
*/

export default {
  name: "Indicator",
  components: {
    IndicatorDisclaimerCard,
    KnowledgeBaseLink,
    LineChart,
    PerformanceTable,
    DateRangePicker,
    AreaChart,
    GaugeChart,
    StackedAreaChart,
    VxTimeline,
    "download-as-csv": DownloadAsCSV,
  },
  props: {
    // The name of the indicator (it will be displayed).
    name: {
      type: String,
      required: true,
    },
    // The term of the knowledge base used for this indicator (if any).
    knowledgeBaseTerm: {
      type: String,
      required: false,
    },
    hasFullAccess: {
      type: Boolean,
      required: true,
    },
    // Whether we are still waiting on all the props to be updated by the parent
    // component.
    loading: {
      type: Boolean,
      default: true,
    },
    // Object containing a 'start', 'end', and 'ytdStart' field describing the earliest and latest
    // dates available for analysis.
    dates: {
      type: Object,
      required: true,
    },
    // The current level of the risk indicator.
    indicatorCurrentLevel: {
      type: Number,
    },
    // Array of objects containing the following fields: date, value.
    dataIndicator: {
      type: Array,
    },
    // Array of strings being the names of the different strategies or benchmarks.
    strategiesNames: {
      type: Array,
    },
    // Array of colors for the different strategies.
    strategiesColors: {
      type: Array,
    },
    // Array of objects containing the fields 'name', 'format', 'tooltip' (optional),
    // and another field for each strategy/benchmark we want to show in our performance
    // table.
    performanceStats: {
      type: Array,
    },
    // Array of objects containing the following fields: 'date', and 1 for each
    // strategy/benchmark with the value being the cumulative returns of the given
    // strategy/benchmark for the given date, and a last field 'exposure_value' being the
    // exposure we had on this day in the underlying market.
    cumulativeReturnsWithExposureAndRiskLevel: {
      type: Array,
    },
    // TODO
    exposureData: {
      type: Array,
    },
    // TODO
    outperformanceData: {
      type: Array,
    },
    // TODO
    drawdownsDataWithExposure: {
      type: Array,
    },
    biggestDrawdowns: {
      type: Array,
    },
    underlyingName: {
      type: String,
    },
    // List of events that we would like to allow the user to select for this indicator.
    events: {
      type: Array,
    },
    // The short name of the indicator (if given, it will be displayed between parentheses).
    shortName: {
      type: String,
    },
    // Data of the indicator to download.
    downloadableDataIndicator: {
      type: Array,
    },
    // Data of the end of day exposure to download.
    downloadableDataEndOfDayExposure: {
      type: Array,
    },
    // Extra parameters to provide to the download indicator as csv.
    extraParams: {
      type: Object,
      default() {
        return {};
      },
    },
    // Returns from indicator and market.
    returns: {
      type: Array,
      required: false,
    },
    // Timeline data of the indicator.
    timelineData: {
      type: Array,
      required: false,
    },
    // The label to write down as the title of the card about today's indicator level.
    todayLevelLabel: {
      type: String,
      default: "Today's risk level",
    },
    // Props related to the locked area.
    lockedRedirectUrl: {
      type: String,
    },
    lockedTextContent: {
      type: String,
    },
    isUnderlyingActive: {
      type: Boolean,
      default: true,
    },
    // Function that takes an indicator value and returns the color in which its text
    // should be written in the tooltip.
    getTooltipTextColorFromValue: {
      type: Function,
      default: defaultGetTooltipTextColorFromValue,
    },
    // Function that takes an indicator value and returns the color in which the radial
    // bar should be colored in.
    getRadialColorFromValue: {
      type: Function,
      default: defaultGetRadialColorFromValue,
    },
    // Function that takes an indicator value and returns the color in which the area
    // in the chart should be colored in.
    getRedGradientAreaColorFromValue: {
      type: Function,
      default: defaultGetRedGradientAreaColorFromValue,
    },
    // Function that takes an indicator value and returns the label corresponding to it.
    getLabelFromValue: {
      type: Function,
      default: defaultGetLabelFromValue,
    },
    // Function that takes a name and a value (corresponding to the data we want to put in
    // the line chart) and returns the color of the text in which this value should be written.
    getTooltipColorCumulativeReturns: {
      type: Function,
      default: defaultGetTooltipColorCumulativeReturns,
    },
  },
  metaInfo() {
    return {
      title: `Indicators - ${this.name}`,
    };
  },
  data() {
    return {
      serverErrorMessage: SERVER_ERROR_MESSAGE,
      thresholds: [0, INDICATOR_THRESHOLD_LOW, INDICATOR_THRESHOLD_HIGH],
      period: this.dates,
      exposureColor: exports["colors-exposure"],
      riskLegendColor: defaultGetRedGradientAreaColorFromValue(INDICATOR_THRESHOLD_HIGH), //exports["colors-danger"],
    };
  },
  computed: {
    getFirstPerfDate() {
      if (!this.performanceStats || this.performanceStats.length == 0) return null;
      else return moment(this.performanceStats[0]["first_date"]).format("DD MMM YYYY");
    },
    getLastPerfDate() {
      if (!this.performanceStats || this.performanceStats.length == 0) return null;
      else return moment(this.performanceStats[0]["last_date"]).format("DD MMM YYYY");
    },
    strategiesNamesWithRiskAndExposure() {
      return [
        ...this.strategiesNames,
        "Risk Level",
        `Implied end-of-day exposure using ${this.name}`,
      ];
    },
    strategiesColorsWithRiskAndExposure() {
      return [
        ...this.strategiesColors,
        this.getRedGradientAreaColorFromValue(INDICATOR_THRESHOLD_HIGH),
        "#000000",
      ];
    },
    strategiesFormats() {
      return [",.2f", ",.2f", ",.0%", ",.0%"];
    },
    exposurePropertyName() {
      return EXPOSURE_PROPERTY_NAME;
    },
    riskLevelPropertyName() {
      return RISK_LEVEL_PROPERTY_NAME;
    },
    cumulativeReturnsLineKeyOrder() {
      return ["value", "benchmark_value", this.riskLevelPropertyName, this.exposurePropertyName];
    },
    drawdownsLineKeyOrder() {
      return ["value", "benchmark_value"];
    },
    tablePerfNames() {
      return this.performanceStats?.map((obj) => ({
        name: obj["name"],
        tooltip: obj["tooltip"],
      }));
    },
    tablePerfFormats() {
      return this.performanceStats?.map((obj) => obj["format"]);
    },
    tableStrategiesStats() {
      if (!this.performanceStats) {
        return this.performanceStats;
      }

      return this.cumulativeReturnsLineKeyOrder.slice(0, 2).map((name, idx) => ({
        name: this.strategiesNamesWithRiskAndExposure[idx],
        data: this.performanceStats.map((obj) => obj[name]),
      }));
    },
    shouldHideTodaysLevel() {
      return !this.hasFullAccess;
    },
  },
  watch: {
    loading(newValue) {
      if (newValue) {
        // Start loading in all containers.
        this.$refs["today-indicator"].refreshcard();
        this.$refs["historical-results"].refreshcard();
      } else {
        // Stop loading in all containers.
        this.$refs["today-indicator"].removeRefreshAnimation(200);
        this.$refs["historical-results"].removeRefreshAnimation(200);
      }
    },
    dates(newValue) {
      // When the dates are changing, the zoom picker needs to recompute what
      // a user can reasonnably access. E.g., we might have 5 years of data
      // for the S&P500, but likely not for some ARK ETF, hence the "5Y" needs
      // to be available/be removed if needed. On top of that, this will reset
      // what is behind the "Full period" button to be the correct start/end
      // dates of the currently selected benchmark.
      REFS_ZOOMABLE_CHARTS.forEach((value) => {
        if (this.$refs[value] && this.$refs[value].$refs["zoom-picker"]) {
          this.$refs[value].$refs["zoom-picker"].generateZoomButtons(newValue);
        }
      });
      this.period = newValue;
    },
  },
  methods: {
    // Change the current zoom button to be sync with every other graphs.
    changeZoomButton(zoomButton) {
      REFS_ZOOMABLE_CHARTS.forEach((value) => {
        if (this.$refs[value] && this.$refs[value].$refs["zoom-picker"]) {
          this.$refs[value].$refs["zoom-picker"].toggleButton(zoomButton);
        }
      });
    },
    // This is called when period is changed from a zoom or datepicker.
    changeDates(period, updateInfo) {
      this.$analytics.logEvent(UPDATE_DATE_EVENT, {
        update_method: updateInfo.method,
        period_name: updateInfo.periodName,
        start_date: period.start,
        end_date: period.end,
      });

      if (period.start == null && period.end == null) {
        period = this.dates;
      }

      this.period = period;
      if (!updateInfo.loc || updateInfo.loc != "DateRangePicker") {
        this.$refs["date-range-picker"].setDates(period);
      }

      if (!updateInfo.loc || updateInfo.loc != "ZoomPicker") {
        if (this.period.start == this.dates.start && this.period.end == this.dates.end) {
          updateInfo.zoomButtonIndex = "reset";
        }
      }

      this.changeZoomButton(updateInfo.zoomButtonIndex);
      this.$emit("dates-change", this.period);
    },
    logTooltip() {
      this.$analytics.logEvent(TOOLTIP_DISPLAYED_EVENT, {
        tooltip: "how_to_use_indicator",
      });
    },
    relativePerfTooltipTextColorFromValue(value) {
      if (value == null) return exports["colors-grey"];
      if (value >= 1.0) return exports["colors-success"];
      return exports["colors-danger"];
    },
    backgroundFromValue(value) {
      if (value == null) return exports["colors-invisible"];
      if (value >= 1.0) return exports["colors-success"];
      return exports["colors-danger"];
    },
    formatDate(dateStr) {
      return moment(dateStr).format("YYYY-MM-DD");
    },
    d3Formatter(fmt) {
      return this.$d3FormatLocale(fmt);
    },
  },
};
</script>
